// @flow

import type {Dispute, PerPage, SearchFilters} from '../../../containers/disputes/types';
import type {_Action, ActionCreator} from '../../types';
import type {DisputeSort, Filter} from '../types';

import {reset} from 'redux-form';
import {getDisputes as getDisputeList, getDisputesCount} from '../../../api/disputes';
import {
  selectSettingsMerchantID,
  selectTableCurrentPage,
  selectTableDisputesSort,
  selectTableFilter
} from '../selectors';
import {make as makeFilter, makeToAPI} from '../tools/filter';

const ROOT_TYPE = '@publisher/support/disputes/table';

export const SET_PAGINATION_CURRENT = `${ROOT_TYPE}/pagination/current/set`;
export const SET_FILTER = `${ROOT_TYPE}/filter/set`;
export const SET_FETCHING = `${ROOT_TYPE}/table/fetching/set`;
export const SET_COUNT = `${ROOT_TYPE}/table/count/set`;
export const SET_DISPUTES = `${ROOT_TYPE}/table/disputes/set`;
export const SET_SORT_CURRENT = `${ROOT_TYPE}/table/sort/set`;

const setPaginationCurrent = (payload = 1): _Action<?number> => ({
  type: SET_PAGINATION_CURRENT,
  payload
});

export const setFilter = (payload?: ?Filter): _Action<?Filter> => ({
  type: SET_FILTER,
  payload
});

const setFetching = (payload): _Action<boolean> => ({
  type: SET_FETCHING,
  payload
});

const setCount = (payload = 0): _Action<?number> => ({
  type: SET_COUNT,
  payload
});

const setDisputes = (payload = []): _Action<?Array<Dispute>> => ({
  type: SET_DISPUTES,
  payload
});

const setSortCurrent = (payload?: ?DisputeSort): _Action<?DisputeSort> => ({
  type: SET_SORT_CURRENT,
  payload
});

const fetchDisputes = (
  merchantID: number,
  params: Filter & {limit: number, offset: number, sort: DisputeSort}
): ActionCreator => dispatch =>
  getDisputeList(merchantID, params)
    .then((disputes: Array<Dispute>) => dispatch(setDisputes(disputes)))
    .catch(() => dispatch(setDisputes([])));

const resetForm = (name: string): ActionCreator => dispatch => dispatch(reset(name));

export const changePage = (current: number, filterForm: string): ActionCreator => (
  dispatch,
  getState
) => {
  const state = getState();
  const filter = selectTableFilter(state);
  const filterForAPI = makeToAPI(filter);
  const sort = selectTableDisputesSort(state);
  const merchantID = selectSettingsMerchantID(state);

  dispatch(resetForm(filterForm));
  dispatch(setFetching(true));
  dispatch(setPaginationCurrent(current));
  dispatch(
    fetchDisputes(merchantID, {
      ...filterForAPI,
      offset: (100: PerPage) * (current - 1),
      limit: (100: PerPage),
      sort: sort.field,
      sortDir: sort.dir
    })
  ).finally(() => dispatch(setFetching(false)));
};

export const changeSort = (sort: DisputeSort, filterForm: string): ActionCreator => (
  dispatch,
  getState
) => {
  const state = getState();
  const filter = selectTableFilter(state);
  const filterForAPI = makeToAPI(filter);
  const current = selectTableCurrentPage(state);
  const merchantID = selectSettingsMerchantID(state);

  dispatch(resetForm(filterForm));
  dispatch(setFetching(true));
  dispatch(
    setSortCurrent({
      field: sort.field,
      dir: sort.dir
    })
  );
  dispatch(
    fetchDisputes(merchantID, {
      ...filterForAPI,
      offset: (100: PerPage) * (current - 1),
      limit: (100: PerPage),
      sort: sort.field,
      sortDir: sort.dir
    })
  ).finally(() => dispatch(setFetching(false)));
};

export const getDisputes = (
  merchantID: number,
  values: SearchFilters
): ActionCreator => dispatch => {
  let filter = makeFilter(values);
  dispatch(setFilter(filter));

  filter = makeToAPI(filter);
  const filterPagination = {
    ...filter,
    offset: 0,
    limit: (100: PerPage)
  };

  dispatch(setFetching(true));

  return Promise.all([
    getDisputeList(merchantID, filterPagination),
    getDisputesCount(merchantID, filter)
  ])
    .then(([disputes, count]) => {
      dispatch(setDisputes(disputes));
      dispatch(setCount(count));
    })
    .catch(() => {
      dispatch(setDisputes([]));
      dispatch(setCount(0));
    })
    .finally(() => dispatch(setFetching(false)));
};

export const refreshDisputes = (
  merchantID: number,
  filter: Filter,
  sort: DisputeSort,
  currentPage: number
): ActionCreator => dispatch => {
  const filterForAPI = makeToAPI(filter);
  const filterPagination = {
    ...filterForAPI,
    offset: (100: PerPage) * (currentPage - 1),
    limit: (100: PerPage),
    sort: sort.field,
    sortDir: sort.dir
  };

  dispatch(setFetching(true));

  return getDisputeList(merchantID, filterPagination)
    .then((disputes: Array<Dispute>) => {
      dispatch(setDisputes(disputes));
    })
    .catch(() => dispatch(setDisputes([])))
    .finally(() => {
      dispatch(setFetching(false));
    });
};

export const resetDisputes = (): ActionCreator => dispatch => {
  dispatch(setCount());
  dispatch(setPaginationCurrent());
  dispatch(setSortCurrent({field: '', dir: ''}));
  dispatch(setDisputes());
};

// Export for use in other modules
export {setDisputes, setFetching};
